/** @jsx jsx */
import { Button, jsx, Inline, Separator, Box } from '@front10/base-elements';
import * as React from 'react';
import { Dropdown } from './Dropdown';
import { MobileBody, MobileFilterDialog, MobileFooter, MobileHeader } from './MobileFilterView';
import { noop } from './utils';
import { useIsMobileView } from '@front10/utils';

interface Props {
  toggleElement: React.ReactElement;
  footer?: any;
  onSave: () => void;
  onReset?: () => void;
  onDismiss?: () => void;
  onOpen?: () => void;
  onOpenPopup?: () => void;
  onScroll?: (e: any) => void;
  onClearBtn?: () => void;
  onBackButtonPress?: () => void;
  label: string;
  enabledDoneButton?: boolean;
  enabledClearButton?: boolean;
  labelBottomBtn?: string;
  labelClearBtn?: string;
  variant?: string;
  title?: string;
  showSeparator?: boolean;
  saveOnClose?: boolean;
  disabledDoneButton?: boolean;
  disabledClearButton?: boolean;
  topElement?: any;
  children?: any;
  mobileView?: boolean;
  customAccessibilityToggleElement?: boolean;
  positionRight?: boolean;
  ariaHiddenFooterButtons?: boolean;
  displayFooter?: boolean;
  isOpenVisible?: boolean;
  tabIndex?: number;
  dataTestId?: string;
  useAsDialog?: boolean;
  dialogAriaLabel?: string;
  focusLockInside?: boolean;
}

export function useMobileFilter({
  onSave = noop,
  onReset = noop,
  onDismiss = noop,
  onOpen = noop
}: Pick<Props, 'onSave' | 'onReset' | 'onDismiss' | 'onOpen'>) {
  const [isOpen, setOpen] = React.useState(false);
  const open = React.useCallback(() => {
    onOpen();
    setOpen(true);
  }, [onOpen]);
  const dismiss = React.useCallback(() => {
    onDismiss();
    setOpen(false);
  }, [onDismiss]);
  const save = React.useCallback(() => {
    onSave();
    setOpen(false);
  }, [onSave]);

  return { isOpen, open, dismiss, save, reset: onReset };
}

export function usePopupFilter(props?: { onClose?: () => void }) {
  const ref = React.useRef<Dropdown>(null);
  const filterRef = React.useRef<any>(null);
  const toggle = React.useCallback(() => ref.current?.toggle(), []);
  const onClose = props?.onClose;
  const close = React.useCallback(() => {
    ref.current?.close();
    onClose?.();
  }, [onClose]);

  return { ref, filterRef, toggle, close };
}

function FlexibleFilter(props: React.PropsWithChildren<Props>, ref: any) {
  const {
    saveOnClose = true,
    showSeparator = false,
    disabledDoneButton = false,
    disabledClearButton = false,
    enabledClearButton = false,
    customAccessibilityToggleElement = false,
    positionRight = false,
    ariaHiddenFooterButtons = false,
    displayFooter = true,
    tabIndex = 0,
    focusLockInside = false
  } = props;
  let isMobile = useIsMobileView();
  if (props.mobileView) {
    isMobile = true;
  }
  const dialog = useMobileFilter({
    onSave: props.onSave,
    onReset: props.onReset,
    onDismiss: props.onDismiss,
    onOpen: props.onOpen
  });

  const [focusedElement, setFocusedElement] = React.useState(true);
  const [shiftKey, setShiftKey] = React.useState(false);

  const popup = usePopupFilter();

  React.useImperativeHandle(
    ref,
    () => ({
      save: dialog.save,
      reset: dialog.reset,
      dismiss: dialog.dismiss,
      isOpen: popup.ref.current?.isOpen,
      close: popup.ref.current?.close
    }),
    [dialog.dismiss, dialog.reset, dialog.save, popup.ref]
  );

  const filter = isMobile
    ? React.cloneElement(props.toggleElement, { onClick: dialog.open })
    : props.toggleElement;

  const isOpen = props.isOpenVisible ?? dialog.isOpen;

  const newMobileView = (
    <React.Fragment>
      {!dialog.isOpen ? filter : null}
      {isOpen && (
        <div
          onScroll={(e: any) => {
            const bottom =
              Math.round(e.target.scrollHeight - e.target.scrollTop) === e.target.clientHeight ||
              Math.round(e.target.scrollHeight - e.target.scrollTop) === e.target.clientHeight - 1;
            if (bottom) {
              if (props.onScroll) {
                props.onScroll(e);
              }
            }
          }}
        >
          <MobileFilterDialog
            label={props.label}
            onDismiss={dialog.dismiss}
            isVisible={dialog.isOpen}
          >
            <MobileHeader
              title={props.title ?? ''}
              onBackButtonPress={() => {
                if (props.onBackButtonPress) {
                  props.onBackButtonPress();
                }
                dialog.dismiss();
              }}
            />
            {props.topElement && <Box p="2">{props.topElement}</Box>}
            <MobileBody>{props.children}</MobileBody>
            <MobileFooter align="end">{props.footer}</MobileFooter>
          </MobileFilterDialog>
        </div>
      )}
    </React.Fragment>
  );
  const mobileView = props.onScroll ? (
    newMobileView
  ) : (
    <React.Fragment>
      {filter}
      {isOpen && (
        <MobileFilterDialog
          label={props.label}
          onDismiss={dialog.dismiss}
          isVisible={dialog.isOpen}
        >
          <MobileHeader
            title={props.title ?? ''}
            onBackButtonPress={() => {
              if (props.onBackButtonPress) {
                props.onBackButtonPress();
              }
              dialog.dismiss();
            }}
          />
          <MobileBody>{props.children}</MobileBody>
          {props.footer
            ? props.footer
            : displayFooter && (
                <MobileFooter align="end">
                  {enabledClearButton && (
                    <Button
                      disabled={disabledClearButton}
                      variant="text"
                      onClick={() => {
                        if (props.onClearBtn) {
                          props.onClearBtn();
                        }
                      }}
                    >
                      {props.labelClearBtn || 'Clear'}
                    </Button>
                  )}

                  <Button disabled={disabledDoneButton} variant="text" onClick={dialog.save}>
                    {props.labelBottomBtn || 'Done'}
                  </Button>
                </MobileFooter>
              )}
        </MobileFilterDialog>
      )}
    </React.Fragment>
  );

  if (isMobile) {
    return mobileView;
  }

  const footer = (
    <Inline variant="layout.popover.footer" gap={2} align="end" pb="11px">
      {enabledClearButton && (
        <Button
          type="button"
          disabled={disabledClearButton}
          variant="text"
          onClick={() => {
            if (props.onClearBtn) {
              props.onClearBtn();
            }
          }}
          aria-hidden={ariaHiddenFooterButtons}
          className="footerButtons"
        >
          {props.labelClearBtn || 'Clear'}
        </Button>
      )}

      <Button
        disabled={disabledDoneButton}
        type="button"
        variant="text"
        onClick={() => {
          if (!saveOnClose) {
            props.onSave();
          }
          popup.ref.current?.close();
        }}
        onFocus={() => {
          setFocusedElement(false);
        }}
        onKeyDown={(e) => {
          setShiftKey(e.shiftKey);
        }}
        onBlur={() => {
          if (!shiftKey && !focusLockInside) {
            popup.filterRef?.current?.focus();
          }
        }}
        aria-hidden={ariaHiddenFooterButtons}
      >
        {props.labelBottomBtn || 'Done'}
      </Button>
    </Inline>
  );

  return (
    <Dropdown
      useDelay={false}
      customAccessibility={customAccessibilityToggleElement}
      ref={popup.ref}
      targetRef={popup.filterRef}
      toggleElement={filter}
      portal={false}
      onClose={() => {
        popup.ref.current?.setOpen(false);
        if (saveOnClose) {
          props.onSave();
        }
        if (!focusedElement) {
          setTimeout(() => popup.filterRef.current?.focus(), 10);
          setFocusedElement(true);
        }
      }}
      onOpenPopup={() => {
        if (props.onOpenPopup) {
          setFocusedElement(false);
          props.onOpenPopup();
        }
      }}
      variant={props.variant ?? 'layout.dropdown'}
      position={positionRight}
      tabIndex={tabIndex}
      dataTestId={props.dataTestId}
      useAsDialog={props.useAsDialog}
      dialogAriaLabel={props.dialogAriaLabel}
      focusLock={focusLockInside}
    >
      {props.children}
      {showSeparator && <Separator aria-hidden="true" />}
      {props.enabledDoneButton && props.footer ? props.footer : footer}
    </Dropdown>
  );
}

const FlexibleFilterContainer = React.forwardRef(FlexibleFilter);

export { FlexibleFilterContainer };
